import { useState, useCallback } from "react";
import { faBrain } from "@fortawesome/free-solid-svg-icons";
import type { DevToolsPlugin } from "../../types";

const MODELS = [
  "hydra-uk-simplybusiness-fqa_0",
  "hydra-uk-simplybusiness-fqa_1",
  "hydra-uk-simplybusiness-fqa_2",
  "hydra-uk-simplybusiness-fqa_10",
  "hydra-uk-moneysupermarket-fqa_0",
  "hydra-us-simplybusiness-qcp",
  "hydra-uk-simplybusiness-qcp",
  "hydragpt-1_rfq_created-simplybusiness_uk",
  "hydra-landing-pages-uk",
];

interface IdmPluginProps {
  domainUserId?: string;
  currentSite?: string;
  journeyId?: string;
  verticalForIdmRequest?: string;
}

function IdmPanel({
  domainUserId,
  currentSite,
  journeyId,
  verticalForIdmRequest,
}: IdmPluginProps) {
  const [model, setModel] = useState(MODELS[0]);
  const [score, setScore] = useState(0.99);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const sendPrediction = useCallback(() => {
    if (!domainUserId || !journeyId || !currentSite) return;
    setSending(true);
    setSent(false);

    const geoRegion = model.includes("uk") ? "uk" : "us";

    fetch("http://localhost:1415/dev_tools/trigger_prediction", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        site: currentSite,
        geoRegion,
        vertical: verticalForIdmRequest,
        journeyId,
        duid: domainUserId,
        score,
        model,
      }),
    })
      .then((r) => r.json())
      .then(() => setSent(true))
      .catch((e) => console.error("IDM prediction error:", e))
      .finally(() => setSending(false));
  }, [
    domainUserId,
    currentSite,
    journeyId,
    verticalForIdmRequest,
    model,
    score,
  ]);

  const enablePolling = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("enableInterventions", "true");
    window.location.href = url.toString();
  }, []);

  if (!domainUserId || !journeyId || !currentSite) {
    return (
      <div className="dt-status-banner dt-status-banner--warning">
        <div className="dt-status-banner__title">IDM unavailable</div>
        <div className="dt-status-banner__text">
          Requires active journey, DUID, and site
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dt-idm__description">
        Trigger an IDM prediction for the current journey session.
      </div>

      <button
        className="dt-btn"
        onClick={enablePolling}
        type="button"
        style={{ width: "100%", marginBottom: 16 }}
      >
        Enable Intervention Polling
      </button>

      <div className="dt-section-label">Trigger Prediction</div>

      <div className="dt-form-field">
        <label className="dt-form-label" htmlFor="dt-idm-model-select">
          Model
        </label>
        <select
          id="dt-idm-model-select"
          className="dt-form-select"
          value={model}
          onChange={(e) => setModel(e.target.value)}
        >
          {MODELS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      <div className="dt-idm__score-row">
        <div className="dt-form-field">
          <label className="dt-form-label" htmlFor="dt-idm-score-input">
            Score (0.00 - 1.00)
          </label>
          <input
            id="dt-idm-score-input"
            className="dt-form-input"
            type="number"
            min="0.00"
            max="1.00"
            step="0.01"
            value={score}
            onChange={(e) => setScore(parseFloat(e.target.value))}
          />
        </div>
        <button
          className="dt-btn dt-btn--primary"
          onClick={sendPrediction}
          disabled={sending}
          type="button"
        >
          {sending ? "Sending..." : "Send"}
        </button>
      </div>

      {sent && (
        <div className="dt-status-banner" style={{ marginTop: 12 }}>
          <div className="dt-status-banner__title">Prediction sent</div>
          <div className="dt-status-banner__text">
            {model} @ {score}
          </div>
        </div>
      )}
    </>
  );
}

export function idmPlugin(props: IdmPluginProps): DevToolsPlugin {
  return {
    id: "idm",
    name: "IDM Prediction",
    icon: faBrain,
    render: () => <IdmPanel {...props} />,
  };
}
